/**
 * ScanStatusBox - Clickable status indicator that opens details modal
 *
 * Displays a compact status box (88x153px) showing scan status.
 * The entire box is clickable to open the details modal.
 *
 * Features:
 * - Compact 88x153px display
 * - StatusChip showing current status or "No scans"
 * - Hover state indicates interactivity
 * - Disabled state when no scans available
 *
 * Used in Asset, Vulnerability, and Capability drawers.
 */

import { MinusCircleIcon } from '@heroicons/react/24/outline';

import { Loader } from '@/components/Loader';
import { StatusChip, StatusChipProps } from '@/components/legacy-ui/StatusChip';
import { cn } from '@/utils/classname';

import { ScanStatusBoxProps } from './types';

/**
 * Default status configuration for when no scans exist
 */
const noScansConfig: StatusChipProps = {
  label: 'No scans',
  variant: 'muted',
  icon: <MinusCircleIcon className="size-3 shrink-0" />,
};

/**
 * ScanStatusBox Component
 *
 * Renders a clickable box displaying scan status. Clicking opens
 * the details modal to view all scan jobs.
 */
export function ScanStatusBox({
  statusConfig,
  isLoading,
  onViewDetails,
  disabled = false,
  title = 'Scan Status',
}: ScanStatusBoxProps) {
  const isDisabled = disabled || !statusConfig;

  return (
    <button
      type="button"
      onClick={onViewDetails}
      disabled={isDisabled}
      className={cn(
        'bg-chariot-background-secondary flex h-[88px] w-[153px] shrink-0 flex-col items-center justify-center gap-1 rounded-md px-4',
        'transition-colors duration-150',
        'focus-visible:ring-chariot-brand-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
        !isDisabled && 'hover:bg-chariot-background-tertiary cursor-pointer',
        isDisabled && 'cursor-not-allowed'
      )}
      aria-label={`View ${title} details`}
    >
      <p className="text-chariot-text-primary text-xs font-semibold">{title}</p>
      <Loader isLoading={isLoading} className="h-[22px] w-[90px] rounded-full">
        <div className="flex h-[22px] w-[90px] items-center justify-center">
          <StatusChip {...(statusConfig ?? noScansConfig)} />
        </div>
      </Loader>
    </button>
  );
}
